const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export const buildMoveMessage = (guest, table, tables) => {
    const guestName = capitalizeWords(guest.fullName);
    const tableName = capitalizeWords(table.name);
    const registered = guest.companions?.registered || [];
    const unregistered = guest.companions?.unregistered || 0;
    const hasGroup = registered.length > 0 || unregistered > 0;
    const currentTableName = (() => {
        if (!guest.reservedTableId) return null;
        const id = guest.reservedTableId?._id || guest.reservedTableId;
        return capitalizeWords(tables.find(t => t._id === id)?.name || '');
    })();

    const lines = [];
    lines.push(`Premjestiti ${guestName} za stol "${tableName}"?`);
    if (currentTableName) lines.push(`Trenutni stol: ${currentTableName}`);
    if (hasGroup) {
        lines.push('');
        lines.push('⚠️ Gost ima grupu:');
        lines.push(`• ${guestName} (nositelj) → premješta se`);
        registered.forEach(r => lines.push(`• ${capitalizeWords(r.fullName || String(r))} → ostaje za trenutnim stolom`));
        if (unregistered > 0) lines.push(`• +${unregistered} dodatnih mjesta → premještaju se zajedno`);
    }
    return lines.join('\n');
};

export const buildRemoveMessage = (guest, tables) => {
    const guestName = capitalizeWords(guest.fullName);
    const registered = guest.companions?.registered || [];
    const unregistered = guest.companions?.unregistered || 0;
    const hasGroup = registered.length > 0 || unregistered > 0;
    const currentTableName = (() => {
        if (!guest.reservedTableId) return null;
        const id = guest.reservedTableId?._id || guest.reservedTableId;
        return capitalizeWords(tables.find(t => t._id === id)?.name || '');
    })();

    const lines = [];
    lines.push(`Ukloniti rezervaciju za ${guestName}?`);
    if (currentTableName) lines.push(`Stol: ${currentTableName}`);
    if (hasGroup) {
        lines.push('');
        lines.push('⚠️ Gost ima grupu — samo nositelju se uklanja rezervacija:');
        lines.push(`• ${guestName} → gubi rezervaciju`);
        registered.forEach(r => lines.push(`• ${capitalizeWords(r.fullName || String(r))} → ostaje za stolom`));
        if (unregistered > 0) lines.push(`• +${unregistered} dodatnih mjesta → uklanja se rezervacija`);
    }
    return lines.join('\n');
};

export const buildRsvpMessage = (guest, isAttending) => {
    const guestName = capitalizeWords(guest.fullName);
    const registered = guest.companions?.registered || [];
    const unregistered = guest.companions?.unregistered || 0;
    const hasGroup = registered.length > 0 || unregistered > 0;

    if (isAttending === null) {
        return `Poništiti RSVP za ${guestName}?\nStatus će se resetirati na "Nije odgovorio".`;
    }

    const action = isAttending ? 'potvrditi dolazak' : 'označiti kao odsutan';
    const lines = [`Žeite li ${action} za ${guestName}?`];

    if (hasGroup && !isAttending) {
        lines.push('');
        lines.push('⚠️ Gost ima grupu. Označavanjem odsutnosti:');
        lines.push(`• ${guestName} → označen/a kao odsutan/na`);
        registered.forEach(r => lines.push(`• ${capitalizeWords(r.fullName || String(r))} → ostaje nepromijenjen`));
        if (unregistered > 0) lines.push(`• +${unregistered} dodatnih mjesta → uklanja se rezervacija`);
    }

    return lines.join('\n');
};

export const buildDeleteMessage = (guest) => {
    const guestName = capitalizeWords(guest.fullName);
    const registered = guest.companions?.registered || [];
    const unregistered = guest.companions?.unregistered || 0;
    const hasGroup = registered.length > 0 || unregistered > 0;

    const lines = [`Obrisati gosta ${guestName}?`];
    if (hasGroup) {
        lines.push('');
        lines.push('⚠️ Gost ima grupu — brisanjem nositelja:');
        registered.forEach(r => lines.push(`• ${capitalizeWords(r.fullName || String(r))} → izlazi iz grupe`));
        if (unregistered > 0) lines.push(`• +${unregistered} dodatnih mjesta → bit će uklonjeno`);
    }
    return lines.join('\n');
};

export const buildFamilyTableMoveMessage = (family, table, tables) => {
    const familyName = capitalizeWords(family.fullName);
    const tableName = capitalizeWords(table.name);
    const attendingCount = family.familyMembers.filter(m => m.attending === true).length;
    const currentTableName = (() => {
        if (!family.reservedTableId) return null;
        const id = family.reservedTableId?._id || family.reservedTableId;
        return capitalizeWords(tables.find(t => t._id === id)?.name || '');
    })();

    const lines = [`Dodijeliti obitelji ${familyName} stol "${tableName}"?`];
    if (currentTableName) lines.push(`Trenutni stol: ${currentTableName}`);
    lines.push(`Rezervira se ${attendingCount} mjesta za članove koji dolaze.`);
    return lines.join('\n');
};

export const buildFamilyTableRemoveMessage = (family, tables) => {
    const familyName = capitalizeWords(family.fullName);
    const attendingCount = family.familyMembers.filter(m => m.attending === true).length;
    const currentTableName = (() => {
        if (!family.reservedTableId) return null;
        const id = family.reservedTableId?._id || family.reservedTableId;
        return capitalizeWords(tables.find(t => t._id === id)?.name || '');
    })();

    const lines = [`Ukloniti rezervaciju za obitelj ${familyName}?`];
    if (currentTableName) lines.push(`Stol: ${currentTableName}`);
    if (attendingCount > 0) {
        lines.push(`Oslobodit će se ${attendingCount} mjesta.`);
    }
    return lines.join('\n');
};

export const buildFamilyMemberStatusMessage = (family, memberName, attending) => {
    const familyName = capitalizeWords(family.fullName);
    const memberCapitalized = capitalizeWords(memberName);

    if (attending === null) {
        return `Poništiti status za ${memberCapitalized} (obitelj ${familyName})?\nStatus će se resetirati na "Nije odgovorio".`;
    }

    const action = attending ? 'potvrditi dolazak' : 'označiti kao odsutnog/u';
    return `Žele li ${action} za ${memberCapitalized} (obitelj ${familyName})?`;
};

export const buildFamilyDeleteMessage = (family) => {
    const familyName = capitalizeWords(family.fullName);
    const attendingCount = family.familyMembers.filter(m => m.attending === true).length;

    const lines = [`Obrisati obitelj ${familyName}?`];
    lines.push(`Brišu se svi članovi obitelji (${family.familyMembers.length} osoba).`);
    if (family.reservedTableId && attendingCount > 0) {
        lines.push(`Oslobodit će se ${attendingCount} mjesta na stolu.`);
    }
    return lines.join('\n');
};
